import { message } from "antd";
import moment from "moment";
import React from "react";
import { ApiClientContext } from "../../../../../App";
import useInterval from "../../../../../_core/hooks/useInterval";
import { TimeTrackingUserDataContext } from "../../TimeTrackingUserDataProvider";
import { Project } from "../../types";

import {
  convertToLocal,
  getDates,
  getPassedSeconds,
  prepareSessionForTracking,
  validateTrackerForm,
} from "./utils/helpers";
import {
  ITrackerContext,
  TrackerForm,
  TrackingType,
  TypeOfTrackingLocalStorageKey,
  defaultTrackerForm,
} from "./utils/types";

export const useTracker = (defaultProjectId?: string): ITrackerContext => {
  const { apiClient } = React.useContext(ApiClientContext);
  const { projects, updateSessionWeeks } = React.useContext(
    TimeTrackingUserDataContext,
  );

  const isInitialising = React.useRef<boolean>(true);

  const [startTime, setStartTime] = React.useState<string>(
    moment().format("HH:mm"),
  );
  const [endTime, setEndTime] = React.useState<string>(
    moment().add(30, "minutes").format("HH:mm"),
  );

  const [typeOfTracking, setTypeOfTracking] = React.useState<TrackingType>(
    (localStorage.getItem(TypeOfTrackingLocalStorageKey) ||
      "Auto") as TrackingType,
  );
  const [trackerLoading, setTrackerLoading] = React.useState<boolean>(false);
  const [isTracking, setIsTracking] = React.useState<boolean>(false);
  const [delay, setDelay] = React.useState<number | null>(null);
  const [liveTime, setLiveTime] = React.useState<number>(0);
  const [day, setDay] = React.useState(moment());
  const [formData, setFormData] =
    React.useState<TrackerForm>(defaultTrackerForm);

  const validateSessionCreation = () => {
    const validationResult = validateTrackerForm(
      formData,
      typeOfTracking,
      endTime,
    );
    if (!validationResult.isValid) {
      return validationResult.errorMessage;
    }
    return null;
  };

  const createSession = async (
    startTimeProps?: string,
    endTimeProps?: string,
    dayProps?: moment.Moment,
  ) => {
    const validationError = validateSessionCreation();
    if (validationError) {
      message.error(validationError);
      return;
    }
    const isDateAllowed = (d: moment.Moment): boolean => {
      const now = moment();
      if (d.isAfter(now, "day")) return false;
      const isCurrentMonth =
        d.year() === now.year() && d.month() === now.month();
      if (isCurrentMonth) return true;
      const lastMonth = now.clone().subtract(1, "month");
      const isLastMonth =
        d.year() === lastMonth.year() && d.month() === lastMonth.month();
      return isLastMonth && now.date() <= 5;
    };

    let dates = {};
    const effectiveDay = dayProps || day;
    if (!isDateAllowed(effectiveDay)) {
      message.error(
        "You can only create sessions in the current month, or last month within the first 5 days.",
      );
      return;
    }

    if (startTimeProps && endTimeProps) {
      dates = getDates(startTimeProps, endTimeProps, dayProps);
    } else dates = getDates(startTime, endTime, day);
    const basicSession = prepareSessionForTracking(formData);

    const preparedSession = {
      ...basicSession,
      ...dates,
    };
    setTrackerLoading(true);

    try {
      await apiClient
        .post("/api/time-tracking/sessions/create", preparedSession)
        .then((res) => {
          message.success("Session recorded.");
        })
        .catch((err) => {
          message.error("Could not record session. " + err);
        });

      await updateSessionWeeks();
    } catch (error) {
      message.error("Could not record session. " + error);
    }
    setTrackerLoading(false);

    setIsTracking(false);
  };

  const switchTracker = (type: TrackingType) => {
    if (!isTracking) {
      setTypeOfTracking(type);
      localStorage.setItem(TypeOfTrackingLocalStorageKey, type);
    }
  };

  const runTimer = (isTracking: boolean) => {
    if (isTracking) {
      setDelay(1000);
    } else {
      setDelay(null);
    }
  };

  const startTracking = async (projectId?: string) => {
    setTrackerLoading(true);
    let formDataCurrent = formData;
    setTypeOfTracking("Auto");
    if (projectId) {
      formDataCurrent = {
        ...formData,
        projectId: projectId,
      };
    }
    const validationResult = validateTrackerForm(
      formDataCurrent,
      typeOfTracking,
      endTime,
    );
    if (!validationResult.isValid) {
      message.error(validationResult.errorMessage || "Validation errors!");
      return;
    }

    const preparedSession = prepareSessionForTracking(formDataCurrent);

    try {
      await apiClient
        .post("/api/time-tracking/sessions/start", preparedSession)
        .then((_res) => {
          if (projectId) {
            setFormData(formDataCurrent);
          }
          setIsTracking(true);
          runTimer(true);
          setStartTime(moment().format());
        });
    } catch (error: any) {
      message.error(
        "Could not start a new session. " + error?.response?.data?.message,
      );
    }
    setTrackerLoading(false);
  };

  const editCurrentSession = async (start: any) => {
    setTrackerLoading(true);
    await apiClient
      .put(`/api/time-tracking/sessions/update-current-session-start`, {
        start: start,
      })
      .then((res) => {
        setStartTime(start);
        message.success("Session edited.");
      })
      .catch((err) => {
        console.log(err);
        message.error("Could not edit session. " + err);
      });
    setTrackerLoading(false);
  };

  const stopTracking = async () => {
    const err = validateSessionCreation();
    if (err) {
      console.error("error: ", err);
      message.error("Form validation errors");
      return;
    }

    setTrackerLoading(true);
    const end = moment().toISOString();
    await apiClient
      .put("/api/time-tracking/sessions/end", {
        end: end,
      })
      .then(async (res) => {
        await updateSessionWeeks();
        setStartTime(moment().format("HH:mm"));
      })
      .catch(() => {
        message.error("Error ending task");
      });

    runTimer(false);
    setIsTracking(false);
    setLiveTime(0);

    message.success("Session saved.");
    setTrackerLoading(false);
  };

  const onSuccessActiveSession = (response: any) => {
    const activeSession = response.data;
    if (activeSession && Object.keys(activeSession).length !== 0) {
      const startTime = activeSession.start;
      setStartTime(convertToLocal(startTime).format());

      setIsTracking(true);
      setTypeOfTracking("Auto");

      setLiveTime(getPassedSeconds(startTime));
      setFormData({
        description: activeSession.description ?? "",
        projectId: activeSession.projectId,
      });

      runTimer(true);
    } else {
      const currentProject =
        projects.find((p: Project) => p.projectId === formData.projectId) ||
        projects[0];

      setFormData({
        projectId: currentProject?.projectId || "",
        description: "",
      });
    }
  };

  const initializeFormData = async () => {
    await apiClient
      .get("/api/time-tracking/sessions/user/active")
      .then((response) => onSuccessActiveSession(response))
      .catch((err) => {
        console.error("Failed to fetch active session", err);
      })
      .finally(() => {
        isInitialising.current = false;
      });
  };

  const showModal = () => {
    setTypeOfTracking("Calendar");
  };

  const handleClose = () => {
    setTypeOfTracking("Auto");
  };

  React.useEffect(() => {
    if (projects.length) {
      initializeFormData();
    }
  }, [projects]);

  useInterval(() => {
    setLiveTime(getPassedSeconds(startTime));
  }, delay);

  return {
    day,
    formData,
    typeOfTracking,
    isTracking,
    trackerLoading,
    startTime,
    endTime,
    liveTime,
    setDay,
    setFormData,
    setStartTime,
    setEndTime,
    switchTracker,
    createSession,
    startTracking,
    stopTracking,
    editCurrentSession,
    showModal,
    handleClose,
  };
};
